import { z } from 'zod';

import {
  AssemblyLifecycleSchema,
  ChangeOrderStatusSchema,
  ConnectorKindSchema,
  ConnectorStatusSchema,
  PurchaseOrderStatusSchema,
  SourceSystemSchema,
} from '../enums.js';

import { IsoDateSchema, IsoDateTimeSchema, RowErrorSchema, UuidSchema } from './common.js';

// source-service: connectors, assemblies, BOM snapshots, change orders, POs.

export const ConnectorSchema = z.object({
  id: UuidSchema,
  kind: ConnectorKindSchema,
  name: z.string(),
  description: z.string(),
  status: ConnectorStatusSchema,
  /** File path for file sources; null for API sources. Shown in mono. */
  location: z.string().nullable(),
  lastAttemptedReadAt: IsoDateTimeSchema.nullable(),
  lastSuccessfulReadAt: IsoDateTimeSchema.nullable(),
  sourceModifiedAt: IsoDateTimeSchema.nullable(),
  /** Set when the last read found the file unchanged: "unchanged since …" (spec §5.8). */
  unchangedSince: IsoDateTimeSchema.nullable(),
  /** BR-045: failure detail is present only for managers. */
  lastErrorDetail: z.array(RowErrorSchema).nullable().optional(),
});
export type Connector = z.infer<typeof ConnectorSchema>;

export const ConnectorsResponseSchema = z.object({ connectors: z.array(ConnectorSchema) });
export type ConnectorsResponse = z.infer<typeof ConnectorsResponseSchema>;

export const AssemblySchema = z.object({
  id: UuidSchema,
  pn: z.string(),
  name: z.string(),
  lifecycle: AssemblyLifecycleSchema,
  assignedEngineerEmail: z.email().nullable(),
  currentPdmRev: z.string().nullable(),
  currentErpRev: z.string().nullable(),
});
export type Assembly = z.infer<typeof AssemblySchema>;

export const AssembliesResponseSchema = z.object({ assemblies: z.array(AssemblySchema) });
export type AssembliesResponse = z.infer<typeof AssembliesResponseSchema>;

export const AssemblyIdParamsSchema = z.object({ id: UuidSchema });

export const RevisionsQuerySchema = z.object({ system: SourceSystemSchema });

export const RevisionSchema = z.object({
  id: UuidSchema,
  system: SourceSystemSchema,
  rev: z.string(),
  configuration: z.string(),
  isReleased: z.boolean(),
  readAt: IsoDateTimeSchema,
});
export type Revision = z.infer<typeof RevisionSchema>;

export const RevisionsResponseSchema = z.object({ revisions: z.array(RevisionSchema) });
export type RevisionsResponse = z.infer<typeof RevisionsResponseSchema>;

export const BomQuerySchema = z.object({
  system: SourceSystemSchema,
  /** Defaults to the current revision. */
  rev: z.string().optional(),
  configuration: z.string().default('Default'),
});
export type BomQuery = z.infer<typeof BomQuerySchema>;

export const BomLineSchema = z.object({
  pn: z.string().min(1),
  parentPn: z.string().nullable(),
  parentPath: z.string(),
  description: z.string(),
  /** Per-parent quantity (BR-015). */
  qty: z.number().nonnegative(),
  rev: z.string().nullable(),
  uom: z.string().nullable(),
  findNo: z.string().nullable(),
  seqNo: z.string().nullable(),
  isHighValue: z.boolean(),
});
export type BomLine = z.infer<typeof BomLineSchema>;

export const BomResponseSchema = z.object({
  assemblyId: UuidSchema,
  revisionId: UuidSchema,
  system: SourceSystemSchema,
  rev: z.string(),
  configuration: z.string(),
  readAt: IsoDateTimeSchema,
  lines: z.array(BomLineSchema),
});
export type BomResponse = z.infer<typeof BomResponseSchema>;

/** Sidebar footer: "PDM and ERP last read on …". */
export const FreshnessResponseSchema = z.object({
  pdmLastReadAt: IsoDateTimeSchema.nullable(),
  erpLastReadAt: IsoDateTimeSchema.nullable(),
});
export type FreshnessResponse = z.infer<typeof FreshnessResponseSchema>;

// Internal routes

export const SystemQuerySchema = z.object({ system: SourceSystemSchema });

export const BomAssemblyParamsSchema = z.object({ assemblyId: UuidSchema });

/** `POST /internal/reread/:assemblyId`: what the manual recheck found per connector. */
export const RereadResponseSchema = z.object({
  reads: z.array(
    z.object({
      kind: ConnectorKindSchema,
      outcome: z.enum(['unchanged', 'updated', 'failed']),
      snapshotsCreated: z.number().int().nonnegative(),
    }),
  ),
});
export type RereadResponse = z.infer<typeof RereadResponseSchema>;

export const BomHistoryResponseSchema = z.object({ revisions: z.array(BomResponseSchema) });
export type BomHistoryResponse = z.infer<typeof BomHistoryResponseSchema>;

export const ChangeOrderSchema = z.object({
  id: UuidSchema,
  code: z.string(),
  status: ChangeOrderStatusSchema,
  approvedAt: IsoDateTimeSchema.nullable(),
  coverage: z.array(z.object({ assemblyPn: z.string(), partPn: z.string() })),
});
export type ChangeOrder = z.infer<typeof ChangeOrderSchema>;

export const ChangeOrdersQuerySchema = z.object({
  assemblyPn: z.string().optional(),
  partPn: z.string().optional(),
});

export const ChangeOrdersResponseSchema = z.object({ changeOrders: z.array(ChangeOrderSchema) });
export type ChangeOrdersResponse = z.infer<typeof ChangeOrdersResponseSchema>;

export const PurchaseOrderSchema = z.object({
  id: UuidSchema,
  code: z.string(),
  partPn: z.string(),
  qty: z.number().nonnegative(),
  releaseDate: IsoDateSchema,
  status: PurchaseOrderStatusSchema,
});
export type PurchaseOrder = z.infer<typeof PurchaseOrderSchema>;

export const PurchaseOrdersQuerySchema = z.object({ partPn: z.string().optional() });

export const PurchaseOrdersResponseSchema = z.object({
  purchaseOrders: z.array(PurchaseOrderSchema),
});
export type PurchaseOrdersResponse = z.infer<typeof PurchaseOrdersResponseSchema>;

/** Demo only (`DEMO_MODE=true`): the analyst "raises" a change order. */
export const DevChangeOrderRequestSchema = z.object({
  code: z.string().min(1),
  status: ChangeOrderStatusSchema,
  coverage: z.array(z.object({ assemblyPn: z.string(), partPn: z.string() })).min(1),
});
export type DevChangeOrderRequest = z.infer<typeof DevChangeOrderRequestSchema>;

/** Demo only (`DEMO_MODE=true`): the supply team "fixes" one ERP line via the overlay. */
export const DevErpFixRequestSchema = z.object({
  assemblyId: UuidSchema,
  parentPath: z.string(),
  pn: z.string().min(1),
  qty: z.number().nonnegative(),
});
export type DevErpFixRequest = z.infer<typeof DevErpFixRequestSchema>;
